/**
 * PrizeManagement - CRUD for prizes and raffles
 */
import React, { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Gift, Trophy, Shuffle, Loader2, DollarSign, Upload, X } from 'lucide-react';
import { prizeAPI, getErrorMessage } from '@/lib/api';
import api from '@/lib/api';
import { MediaUpload } from '@/components/MediaUpload';

const ELIGIBILITY = ['all', 'checked_in', 'vip'];

// Form component extracted outside
const PrizeForm = ({ prize, onSubmit, onCancel }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState(prize || { name: '', description: '', image_url: '', value: '', quantity: 1, eligibility: 'all', draw_time: '' });

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('file_type', 'general');

      const response = await api.post('/admin/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data?.url) {
        const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
        const fullUrl = `${baseUrl}${response.data.url}`;
        setForm({...form, image_url: fullUrl});
        toast.success('Image uploaded!');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <div><Label>Prize Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="MacBook Pro" /></div>
      <div><Label>Description</Label><Textarea value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} rows={2} /></div>
      
      {/* Image upload section */}
      <div className="space-y-2">
        <MediaUpload value={form.image_url || ''} onChange={(url) => setForm({ ...form, image_url: url })} purpose="photo" label="Prize image" testId="prize-image-upload" previewClassName="h-20" />
      </div>

      <div><Label>Value ($)</Label><Input type="number" value={form.value || ''} onChange={e => setForm({...form, value: parseFloat(e.target.value) || ''})} /></div>
      <div className="grid grid-cols-2 gap-4">
        <div><Label>Quantity</Label><Input type="number" min="1" value={form.quantity} onChange={e => setForm({...form, quantity: parseInt(e.target.value) || 1})} /></div>
        <div><Label>Eligibility</Label>
          <Select value={form.eligibility} onValueChange={v => setForm({...form, eligibility: v})}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{ELIGIBILITY.map(e => <SelectItem key={e} value={e}>{e === 'all' ? 'All Attendees' : e === 'checked_in' ? 'Checked In Only' : 'VIP Only'}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label>Draw Time (optional)</Label><Input type="datetime-local" value={form.draw_time ? new Date(form.draw_time).toISOString().slice(0, 16) : ''} onChange={e => setForm({...form, draw_time: e.target.value})} /></div>
      <div className="flex justify-end gap-2 pt-4">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button onClick={() => onSubmit(form)} disabled={!form.name}>{prize ? 'Update' : 'Create'}</Button>
      </div>
    </div>
  );
};

const PrizeManagement = ({ eventId }) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editingPrize, setEditingPrize] = useState(null);
  const [drawingPrize, setDrawingPrize] = useState(null);
  const [winner, setWinner] = useState(null);

  const { data: prizes = [], isLoading } = useQuery({
    queryKey: ['prizes', eventId],
    queryFn: () => prizeAPI.list(eventId).then(r => r.data),
  });

  const createMutation = useMutation({
    mutationFn: (data) => prizeAPI.create(eventId, data),
    onSuccess: () => { toast.success('Prize created'); queryClient.invalidateQueries(['prizes', eventId]); setIsOpen(false); },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed'))
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => prizeAPI.update(eventId, id, data),
    onSuccess: () => { toast.success('Prize updated'); queryClient.invalidateQueries(['prizes', eventId]); setEditingPrize(null); },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed'))
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => prizeAPI.delete(eventId, id),
    onSuccess: () => { toast.success('Prize deleted'); queryClient.invalidateQueries(['prizes', eventId]); },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed'))
  });

  const drawMutation = useMutation({
    mutationFn: (prizeId) => prizeAPI.draw(eventId, prizeId),
    onSuccess: (data) => { setWinner(data.data); queryClient.invalidateQueries(['prizes', eventId]); toast.success('Winner drawn!'); },
    onError: (err) => toast.error(getErrorMessage(err, 'Failed to draw winner'))
  });

  if (isLoading) return <div className="flex justify-center p-8"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-6" data-testid="prize-management">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold flex items-center gap-2"><Gift className="h-6 w-6" />Prizes & Raffles</h2>
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogTrigger asChild><Button><Plus className="h-4 w-4 mr-2" />Add Prize</Button></DialogTrigger>
          <DialogContent><DialogHeader><DialogTitle>Add Prize</DialogTitle></DialogHeader>
            <PrizeForm onSubmit={data => createMutation.mutate(data)} onCancel={() => setIsOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {prizes.map(prize => (
          <Card key={prize.id} data-testid={`prize-${prize.id}`} className="overflow-hidden">
            {prize.image_url && <img src={prize.image_url} alt={prize.name} className="w-full h-32 object-cover" />}
            <CardHeader className="pb-2">
              <div className="flex justify-between items-start">
                <CardTitle className="text-lg">{prize.name}</CardTitle>
                <Badge variant={prize.status === 'available' ? 'default' : 'secondary'}>{prize.status}</Badge>
              </div>
            </CardHeader>
            <CardContent>
              {prize.description && <p className="text-sm text-slate-600 line-clamp-2 mb-2">{prize.description}</p>}
              <div className="flex items-center gap-3 text-sm text-slate-500 mb-3">
                {prize.value && <span className="flex items-center gap-1"><DollarSign className="h-4 w-4" />{prize.value}</span>}
                <span>{prize.claimed_count}/{prize.quantity} claimed</span>
              </div>
              <div className="flex items-center justify-between">
                <Button variant="outline" size="sm" onClick={() => { setDrawingPrize(prize); setWinner(null); }} disabled={prize.status !== 'available' || prize.claimed_count >= prize.quantity}>
                  <Shuffle className="h-4 w-4 mr-1" />Draw Winner
                </Button>
                <div className="flex gap-1">
                  <Button variant="ghost" size="sm" onClick={() => setEditingPrize(prize)}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => deleteMutation.mutate(prize.id)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {prizes.length === 0 && <p className="col-span-3 text-center text-slate-500 py-8">No prizes added yet.</p>}
      </div>

      <Dialog open={!!editingPrize} onOpenChange={() => setEditingPrize(null)}>
        <DialogContent><DialogHeader><DialogTitle>Edit Prize</DialogTitle></DialogHeader>
          {editingPrize && <PrizeForm prize={editingPrize} onSubmit={data => updateMutation.mutate({ id: editingPrize.id, data })} onCancel={() => setEditingPrize(null)} />}
        </DialogContent>
      </Dialog>

      <Dialog open={!!drawingPrize} onOpenChange={() => { setDrawingPrize(null); setWinner(null); }}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Trophy className="h-5 w-5 text-yellow-500" />Draw Winner</DialogTitle></DialogHeader>
          {drawingPrize && (
            <div className="text-center py-6">
              <h3 className="text-xl font-bold mb-4">{drawingPrize.name}</h3>
              {winner ? (
                <div className="animate-bounce">
                  <Trophy className="h-16 w-16 mx-auto text-yellow-500 mb-4" />
                  <p className="text-2xl font-bold text-green-600">{winner.winner_name}</p>
                  <p className="text-sm text-slate-500 mt-2">Congratulations!</p>
                </div>
              ) : (
                <Button size="lg" onClick={() => drawMutation.mutate(drawingPrize.id)} disabled={drawMutation.isPending}>
                  {drawMutation.isPending ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Shuffle className="h-5 w-5 mr-2" />}
                  Draw Random Winner
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrizeManagement;
